import React, { useState } from "react";
import { ChevronDown, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { MolecularBg } from "../components/MolecularBg";
import { useSEO } from "../lib/useSEO";

/**
 * Carbon Labs FAQ — adapted from the Aussie Peptides reference content the
 * client provided. All references rewired to Carbon Labs branding, AU domain,
 * Stripe payments, and the Carbon Labs catalogue (KLOW, Retatrutide, NAD+,
 * MOTS-C, Ipamorelin, CJC-1295, Melanotan 2, 5-Amino-1MQ, Selank, Semax,
 * GHRP-2, HCG, Bacteriostatic Water).
 */

const CONTACT_EMAIL = "carbonlabsau@outlook.com";

const BAC_TABLE = [
    { product: "KLOW blend", vial: "80 mg", water: "3 mL" },
    { product: "Retatrutide", vial: "10 mg", water: "1 mL" },
    { product: "Retatrutide", vial: "20 mg", water: "2 mL" },
    { product: "MOTS-C", vial: "10 mg", water: "3 mL" },
    { product: "NAD+", vial: "500 mg", water: "5 mL" },
    { product: "Ipamorelin", vial: "10 mg", water: "3 mL" },
    { product: "CJC-1295 No DAC", vial: "10 mg", water: "3 mL" },
    { product: "Melanotan 2", vial: "10 mg", water: "3 mL" },
    { product: "5-Amino-1MQ", vial: "5 mg", water: "3 mL" },
    { product: "Selank", vial: "10 mg", water: "3 mL" },
    { product: "Semax", vial: "10 mg", water: "3 mL" },
    { product: "GHRP-2", vial: "10 mg", water: "3 mL" },
    { product: "HCG", vial: "5000 IU", water: "3 mL" },
];

const SECTIONS = [
    {
        title: "Most Asked Questions",
        items: [
            {
                q: "How much bacteriostatic (BAC) water do I add — and how much per product?",
                a: (
                    <div className="space-y-4">
                        <p>
                            The right amount is product-specific. The exact reconstitution maths for your vial is
                            included with every order and printed on the vial label. As a rule of thumb, almost
                            every vial we stock takes <strong className="text-white">up to 3 mL</strong> of BAC
                            water — the only exception is the mg-dosed <strong className="text-white">NAD+ 500 mg</strong> vial, which takes more.
                        </p>
                        <div className="overflow-x-auto rounded-xl border border-[var(--gold)]/15">
                            <table className="w-full text-sm">
                                <thead className="bg-[var(--gold)]/8 text-[var(--gold)]">
                                    <tr>
                                        <th className="text-left p-3 font-display font-semibold">Product</th>
                                        <th className="text-left p-3 font-display font-semibold">Vial</th>
                                        <th className="text-left p-3 font-display font-semibold">BAC water</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {BAC_TABLE.map((r, i) => (
                                        <tr key={i} className="border-t border-white/5">
                                            <td className="p-3 text-white font-medium">{r.product}</td>
                                            <td className="p-3 text-[var(--text-secondary)]">{r.vial}</td>
                                            <td className="p-3 text-[var(--gold)] font-semibold">{r.water}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="text-xs text-[var(--text-secondary)] italic">
                            Different vial sizes use different volumes — always follow the reconstitution figure on
                            your vial label if it differs from the table above. Add the water slowly down the side
                            of the vial and swirl gently; never shake.
                        </p>
                    </div>
                ),
            },
            {
                q: "I paid for Express shipping but tracking shows a longer delivery window — why?",
                a: (
                    <p>
                        Express speeds up transit once your parcel is dispatched — you can confirm yours actually
                        went out on an Express service by opening the AusPost tracking link in your shipping email
                        and checking the consignment details section, which shows the service used (Express or
                        Standard). Two things worth knowing: AusPost's estimated delivery dates are estimates only
                        — they're often conservative and parcels frequently arrive sooner than the window shown.
                        State-based public holidays can also push an estimate out by a day or two. If the tracking
                        details show a Standard service when you paid for Express, email us at{" "}
                        <a className="text-[var(--gold)] underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>{" "}
                        with your order number and we'll sort it out.
                    </p>
                ),
            },
            {
                q: "How do I know you've received my payment?",
                a: (
                    <p>
                        When your payment is received and matched, we send you an email with the subject line{" "}
                        <em>"Order Confirmed"</em> — that's your confirmation that payment has cleared and your
                        order is locked in for dispatch. Stripe payments (cards, Apple Pay, Google Pay, Link)
                        are confirmed instantly. If you don't see the confirmation email within a few minutes,
                        check your spam folder, then reply to your order email or contact us at{" "}
                        <a className="text-[var(--gold)] underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                    </p>
                ),
            },
        ],
    },
    {
        title: "Ordering & Payment",
        items: [
            {
                q: "How do I place an order?",
                a: (
                    <p>
                        Browse our catalogue, add the compounds you need to your cart, and proceed to checkout.
                        You'll provide a shipping address and pay securely via Stripe. All orders are reviewed
                        before dispatch to confirm research eligibility.
                    </p>
                ),
            },
            {
                q: "What payment methods do you accept?",
                a: (
                    <p>
                        We accept all Stripe-supported methods: credit/debit cards (Visa, Mastercard, Amex), Apple Pay,
                        Google Pay, Link, Afterpay, and Zip. Payments are processed securely — we do not store full
                        card details. Payment must be cleared in full before an order is dispatched.
                    </p>
                ),
            },
            {
                q: "Can I modify or cancel my order after placing it?",
                a: (
                    <p>
                        We can change or cancel an order right up until it goes to our fulfilment team for packing;
                        once it's being packed we can no longer pull it back. Contact us as soon as possible at{" "}
                        <a className="text-[var(--gold)] underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>{" "}
                        with your order reference (VTX-XXXXXXXX).
                    </p>
                ),
            },
            {
                q: "Do you charge GST?",
                a: (
                    <p>
                        Yes. All prices on our site are GST-inclusive — the 10% Australian GST is built into the
                        listed price, so you don't pay any extra at checkout. Your order confirmation shows the
                        GST portion of the total for your records. If you need a tax invoice, contact us at{" "}
                        <a className="text-[var(--gold)] underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>{" "}
                        and we'll issue one.
                    </p>
                ),
            },
            {
                q: "Do you offer bulk order pricing?",
                a: (
                    <p>
                        Yes — we offer discounted pricing on larger quantities and have a{" "}
                        <Link to="/build-a-stack" className="text-[var(--gold)] underline">Build-a-Stack tool</Link>{" "}
                        with automatic stack discounts. For larger institutional orders, email us with what you
                        need and we'll put together a tailored quote.
                    </p>
                ),
            },
        ],
    },
    {
        title: "Shipping & Delivery",
        items: [
            {
                q: "Where do you ship?",
                a: (
                    <p>
                        We currently ship within Australia only. We do not offer international shipping at this
                        time. If you're outside Australia, please check back in the future as we plan to expand
                        our delivery zones.
                    </p>
                ),
            },
            {
                q: "How long does delivery take?",
                a: (
                    <p>
                        In-stock orders placed before 2:00 pm AEST/AEDT on a business day are eligible for same
                        business day dispatch. Orders placed after 2 pm are dispatched the following business day.
                        Standard domestic delivery typically takes 2–5 business days depending on your location.
                        Express options are available at checkout.
                    </p>
                ),
            },
            {
                q: "How are the compounds packaged for shipping?",
                a: (
                    <p>
                        All lyophilised peptides are shipped in secure, discreet, temperature-controlled packaging
                        labelled <em>"For Research Use Only"</em>. No branding on the outer parcel.
                    </p>
                ),
            },
            {
                q: "How can I track my order?",
                a: (
                    <p>
                        Once your order is dispatched you'll receive an email with your Australia Post tracking
                        number. Tracking updates are visible directly through the AusPost tracking link in your
                        shipping email.
                    </p>
                ),
            },
        ],
    },
    {
        title: "Product Quality & Safety",
        items: [
            {
                q: "What purity level are your peptides?",
                a: (
                    <p>
                        Our peptides are manufactured to a typical <strong className="text-white">≥99% purity</strong> by
                        HPLC and Mass Spectrometry. A manufacturer-supplied Certificate of Analysis is available for
                        every product. The COA is provided by the manufacturer, represents the applicable manufacturing
                        batch, and is not unique to each individual vial.
                    </p>
                ),
            },
            {
                q: "Can I get a Certificate of Analysis for my order?",
                a: (
                    <p>
                        Yes. A manufacturer-supplied Certificate of Analysis (COA) is available for each product.
                        These documents are provided by the manufacturer and can be viewed by customers for
                        reference. The COA represents the applicable manufacturing batch and is not unique to each
                        individual vial. You can request the COA for the batch your product was drawn from by
                        contacting us with your order number. See our{" "}
                        <Link to="/lab-standards" className="text-[var(--gold)] underline">Lab Standards</Link>{" "}
                        page for our quality framework.
                    </p>
                ),
            },
            {
                q: "How should I store my peptides?",
                a: (
                    <p>
                        Lyophilised peptides should be stored at <strong className="text-white">2–8°C</strong>{" "}
                        (refrigerated), away from light and moisture. Once reconstituted, solutions should be
                        kept at <strong className="text-white">2–8°C</strong> for short-term use or{" "}
                        <strong className="text-white">-20°C</strong> for long-term storage. Detailed storage
                        instructions are listed on each product page.
                    </p>
                ),
            },
            {
                q: "How do I reconstitute a lyophilised peptide?",
                a: (
                    <p>
                        We recommend reconstituting lyophilised peptides using bacteriostatic water (available
                        in 3 mL and 10 mL vials in our catalogue). Allow the vial to come to room temperature
                        before opening. Add the solvent slowly down the side of the vial and gently swirl — do
                        not shake vigorously. For the exact BAC water volume for your product, refer to the
                        reconstitution table at the top of this page.
                    </p>
                ),
            },
        ],
    },
    {
        title: "Research Use & Compliance",
        items: [
            {
                q: "Who can purchase from Carbon Labs?",
                a: (
                    <p>
                        Our products are sold exclusively to qualified researchers, scientists, and institutions
                        conducting legitimate in-vitro laboratory research. Purchasers must be at least 18 years
                        of age. By placing an order, you confirm that you meet these requirements.
                    </p>
                ),
            },
            {
                q: "Are your products approved for human use?",
                a: (
                    <p>
                        <strong className="text-white">No.</strong> None of the compounds sold by Carbon Labs are
                        approved for human or veterinary consumption. They have not been evaluated by the
                        Therapeutic Goods Administration (TGA) or any equivalent authority for therapeutic use.
                        They are strictly for in-vitro and laboratory research purposes only.
                    </p>
                ),
            },
            {
                q: "Is it legal to purchase research peptides in Australia?",
                a: (
                    <p>
                        Carbon Labs sells peptides in Australia strictly for lawful in-vitro laboratory and
                        analytical research use. That research-use position is different from TGA approval for
                        human use: our products are not approved for human or veterinary consumption, and they
                        are not sold as therapeutic goods. If you're searching for legal peptides in Australia,
                        the key distinction is research use versus human-use approval. Purchasers remain
                        responsible for ensuring their intended use complies with all applicable local, state,
                        and federal laws, and Carbon Labs does not provide legal advice.
                    </p>
                ),
            },
        ],
    },
    {
        title: "Returns & Refunds",
        items: [
            {
                q: "What is your return policy?",
                a: (
                    <p>
                        Due to the nature of research compounds, we do not accept returns of opened or used
                        products. If you receive a damaged, incorrect, or defective item, please contact us
                        within 7 days of receipt with supporting photos and we'll assess your case.
                    </p>
                ),
            },
            {
                q: "My order arrived damaged. What should I do?",
                a: (
                    <p>
                        Please photograph the packaging and product immediately upon receipt and email us at{" "}
                        <a className="text-[var(--gold)] underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>{" "}
                        within 7 days. We'll work with you to arrange a replacement or refund as appropriate.
                    </p>
                ),
            },
        ],
    },
];

// Build flat JSON-LD list from sections (strip JSX, keep text only)
const flatJsonLd = SECTIONS.flatMap((s) =>
    s.items.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: typeof it.a === "string" ? it.a : it.q },
    }))
);

export const FAQPage = () => {
    const [open, setOpen] = useState("0-0");
    useSEO({
        title: "FAQ — Carbon Labs Premium Research Peptides AU",
        description:
            "Frequently asked questions about Carbon Labs research peptides — purity, dispatch, storage, BAC water reconstitution, returns, payment, and research-use compliance.",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: flatJsonLd,
        },
    });

    return (
        <div className="pt-28 pb-20 min-h-screen relative" data-testid="faq-page">
            <MolecularBg intensity="low" />
            <div className="container-x relative z-10 max-w-3xl">
                <span className="eyebrow">// Help center</span>
                <h1 className="font-display text-4xl md:text-6xl font-extrabold text-white tracking-tighter mt-2 mb-3">
                    Frequently Asked Questions
                </h1>
                <p className="text-[var(--text-secondary)] mb-10">
                    Everything you need to know about ordering, shipping, and our research compounds.
                </p>

                {SECTIONS.map((section, si) => (
                    <div key={si} className="mb-10" data-testid={`faq-section-${si}`}>
                        <h2 className="font-display text-xl font-bold text-[var(--gold)] uppercase tracking-wider mb-4">
                            {section.title}
                        </h2>
                        <div className="space-y-3">
                            {section.items.map((f, qi) => {
                                const key = `${si}-${qi}`;
                                const isOpen = open === key;
                                return (
                                    <div
                                        key={key}
                                        className="glass-elevated rounded-2xl overflow-hidden"
                                        data-testid={`faq-item-${si}-${qi}`}
                                    >
                                        <button
                                            onClick={() => setOpen(isOpen ? "" : key)}
                                            className="w-full p-5 flex items-center justify-between text-left"
                                            data-testid={`faq-toggle-${si}-${qi}`}
                                        >
                                            <span className="font-display font-semibold text-white pr-4">{f.q}</span>
                                            <ChevronDown
                                                size={18}
                                                className={`text-[var(--gold)] transition-transform flex-shrink-0 ${
                                                    isOpen ? "rotate-180" : ""
                                                }`}
                                            />
                                        </button>
                                        {isOpen && (
                                            <div className="px-5 pb-5 text-sm text-[var(--text-secondary)] leading-relaxed">
                                                {f.a}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}

                {/* Research Use Only callout */}
                <div
                    className="mt-12 rounded-2xl p-6 md:p-8 border border-[var(--gold)]/30 bg-gradient-to-br from-[rgba(233,188,11,0.06)] to-[rgba(233,188,11,0.02)]"
                    data-testid="research-use-only-callout"
                >
                    <div className="flex items-start gap-4">
                        <div className="shrink-0 w-10 h-10 rounded-full bg-[var(--gold)]/15 flex items-center justify-center">
                            <AlertTriangle className="text-[var(--gold)]" size={20} />
                        </div>
                        <div>
                            <h3 className="font-display text-lg font-bold text-[var(--gold)] mb-2 uppercase tracking-wide">
                                Important: Research Use Only
                            </h3>
                            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                                All products sold by Carbon Labs are intended strictly for in-vitro research and
                                laboratory use only. They are not approved for human or veterinary consumption and
                                are not classified as medicines, supplements, or food products. By purchasing from
                                us, you confirm that products will be used solely for lawful research purposes. We
                                fully support the regulatory framework governing these compounds in Australia and
                                these statements have not been evaluated by the TGA.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="mt-10 text-center">
                    <h3 className="font-display text-xl font-bold text-white mb-2">Still have questions?</h3>
                    <p className="text-[var(--text-secondary)] mb-4">
                        Our team is happy to help with any queries about products, orders, or research support.
                    </p>
                    <Link
                        to="/contact"
                        className="inline-block px-6 py-3 rounded-full bg-[var(--gold)] text-black font-semibold hover:bg-[var(--gold-light)] transition-colors"
                        data-testid="faq-contact-cta"
                    >
                        Contact Us
                    </Link>
                </div>
            </div>
        </div>
    );
};
